import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { useNetworkStore } from '@shared/network';
import { lookupTranslation, type LookupResult } from '../api/workspaceApi';
import { parseLookupError, type LookupErrorReason } from './createErrors';
import { getCachedLookup, setCachedLookup, subscribeLookupForget } from './lookupCache';

/**
 * Состояние живого словарного поиска перевода.
 * - idle: поле пустое / слишком короткое / поиск выключен (ручной режим)
 * - loading: ждём ответ (после дебаунса)
 * - found: слово есть в словаре → показываем переводы, «Создать» доступно
 * - notFound: опечатка / нет статьи → «Создать» блокируется, предлагаем ручной ввод
 * - error: сеть/сервер не ответили; `reason` — почему (ТЗ главной § 10): без связи ждём её
 *   возврата, у 429 и 5xx — «Повторить», 403 — почта не подтверждена
 */
export type LookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'found'; data: LookupResult }
  | { status: 'notFound'; frontName: string }
  | { status: 'error'; reason: LookupErrorReason };

/** Ответ на экране: найдено, нет в словаре или ошибка. */
export type LookupAnswer = Extract<LookupState, { status: 'found' | 'notFound' | 'error' }>;

/** Дополнения для строки создания на главной (ТЗ § 6, § 10, § 15); `/cards/new` их не читает. */
export interface LookupExtras {
  /** Прошлый ответ, пока ищем новый (слово или модуль поменяли): главная держит его на экране
   *  приглушённым, а не прячет лист. Без поиска (idle) — null. */
  previous: LookupAnswer | null;
  /** Ответа нет 4 с — «связь медленная — ещё ищем…». */
  slow: boolean;
  /** Повторить поиск того же слова (после ошибки). */
  retry: () => void;
}

export type TranslationLookup = LookupState & LookupExtras;

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const SLOW_MS = 4000;

const isAnswer = (s: LookupState): s is LookupAnswer =>
  s.status === 'found' || s.status === 'notFound' || s.status === 'error';

/**
 * Дебаунс + кэш + отмена живого поиска перевода. Ключевое требование: запрос НЕ
 * летит на каждую букву — только после паузы `debounceMs` (по умолчанию {@link DEBOUNCE_MS};
 * на сенсорных экранах главная ждёт 400 мс) и при длине ≥ {@link MIN_CHARS}. Предыдущий
 * незавершённый запрос отменяется (AbortController), повтор того же слова берётся из кэша
 * (бэк тоже кэширует, но так нет лишнего round-trip).
 *
 * Сброс слова в кэше (карточка создана, генерация закончилась — forgetLookup) молча
 * перезапрашивает показанный ответ: прежний остаётся на экране до нового, без скелетона.
 * Ошибка «нет связи» повторяется сама, когда сеть вернулась (`useNetworkStore` → reconnected).
 */
export function useTranslationLookup(
  word: string,
  moduleId: string | null,
  enabled: boolean = true,
  { debounceMs = DEBOUNCE_MS }: { debounceMs?: number } = {},
): TranslationLookup {
  const [state, setState] = useState<LookupState>({ status: 'idle' });
  const [previous, setPrevious] = useState<LookupAnswer | null>(null);
  const [slow, setSlow] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const silent = useRef(false);
  // Последний ответ, который был на экране: он становится `previous`, пока ищем новый.
  const shown = useRef<LookupAnswer | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const network = useNetworkStore((s) => s.status);

  useEffect(() => {
    const q = word.trim().toLowerCase();
    return subscribeLookupForget((forgotten) => {
      if (forgotten === q) {
        silent.current = true;
        setRefresh((n) => n + 1);
      }
    });
  }, [word]);

  useEffect(() => {
    const q = word.trim().toLowerCase();
    const quiet = silent.current;
    silent.current = false;

    const apply = (next: LookupState) => {
      if (isAnswer(next)) {
        shown.current = next;
        setPrevious(null);
      } else if (next.status === 'loading') {
        setPrevious(shown.current);
      } else {
        shown.current = null;
        setPrevious(null);
      }
      setState(next);
    };

    if (!enabled || q.length < MIN_CHARS) {
      apply({ status: 'idle' });
      return;
    }

    const cached = getCachedLookup(q, moduleId);
    if (cached) {
      apply(cached.found ? { status: 'found', data: cached } : { status: 'notFound', frontName: q });
      return;
    }

    if (!quiet) apply({ status: 'loading' });
    const ctrl = new AbortController();
    const tid = window.setTimeout(() => {
      lookupTranslation(word, moduleId, ctrl.signal)
        .then((res) => {
          setCachedLookup(q, moduleId, res);
          if (ctrl.signal.aborted) return;
          apply(res.found ? { status: 'found', data: res } : { status: 'notFound', frontName: q });
        })
        .catch((err) => {
          // Отменённый запрос (новая буква / размонтирование) — не ошибка.
          if (ctrl.signal.aborted || (err as { code?: string })?.code === 'ERR_CANCELED') return;
          // Тихое обновление не ломает показанный ответ из-за сбоя сети.
          if (quiet) return;
          const response = err instanceof AxiosError ? err.response : undefined;
          const online = typeof navigator === 'undefined' || navigator.onLine !== false;
          apply({ status: 'error', reason: parseLookupError(response ? response.status : null, response?.data, online) });
        });
    }, quiet ? 0 : debounceMs);

    return () => {
      window.clearTimeout(tid);
      ctrl.abort();
    };
  }, [word, moduleId, enabled, refresh, debounceMs]);

  // 4 с без ответа — «связь медленная». Каждая новая загрузка (буква, модуль) начинает отсчёт заново.
  useEffect(() => {
    if (state.status !== 'loading') {
      setSlow(false);
      return;
    }
    const tid = window.setTimeout(() => setSlow(true), SLOW_MS);
    return () => window.clearTimeout(tid);
  }, [state]);

  const retry = useCallback(() => {
    silent.current = false;
    setRefresh((n) => n + 1);
  }, []);

  // Связь вернулась — повторяем поиск, который упал без ответа (без кнопки «Повторить»).
  useEffect(() => {
    const current = stateRef.current;
    if (network === 'reconnected' && current.status === 'error' && (current.reason === 'offline' || current.reason === 'network')) {
      retry();
    }
  }, [network, retry]);

  return useMemo(() => ({ ...state, previous, slow, retry }), [state, previous, slow, retry]);
}
